import { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../services/auth';

// Type definitions
export interface CreditInfo {
  credits: number;
  maxCredits: number;
  percentage: number;
  tier: 'FREE' | 'PRO';
  baseCredits?: number;
}

interface CreditHookReturn {
  creditInfo: CreditInfo | null;
  isLoading: boolean;
  error: Error | null;
  fetchCreditStatus: () => Promise<CreditInfo | null>;
  processCredits: () => Promise<boolean>;
  refundCredits: () => Promise<void>;
  hasEnoughCredits: () => boolean;
  initializeCreditStatus: () => Promise<void>;
}

const DEBOUNCE_DELAY = 100; // 100ms debounce for requests

export const useCredits = (): CreditHookReturn => {
  const { refreshJWT } = useAuth();
  const [creditInfo, setCreditInfo] = useState<CreditInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  
  // Request tracking refs
  const requestInProgress = useRef(false);
  const pendingRequest = useRef<Promise<CreditInfo | null> | null>(null);
  const debounceTimeout = useRef<NodeJS.Timeout>();

  // Axios instance creation
  const createAxiosInstance = useCallback(async () => {
    const token = await refreshJWT();
    return axios.create({
      baseURL: process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3004' 
        : process.env.REACT_APP_BACKEND_URL,
      withCredentials: true,
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
  }, [refreshJWT]);

  // Main fetch function
  const fetchCreditStatus = useCallback(async () => {
    // Handle concurrent requests
    if (requestInProgress.current) {
      return pendingRequest.current;
    }

    // Clear any existing debounce timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }

    requestInProgress.current = true;
    setIsLoading(true);
    setError(null);

    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.get<CreditInfo>('/credit/status');
      setCreditInfo(response.data);
      return response.data;
    } catch (error) {
      const err = error instanceof Error ? error : new Error('Failed to fetch credit status');
      console.error('Failed to fetch credit status:', error);
      setError(err);
      toast.error('Failed to load credit information');
      return null;
    } finally {
      setIsLoading(false);
      requestInProgress.current = false;
      pendingRequest.current = null;
    }
  }, [createAxiosInstance]);

  // Credit check function
  const hasEnoughCredits = useCallback(() => {
    return creditInfo ? creditInfo.credits >= 2 : false;
  }, [creditInfo]);

  // Process credits function
  const processCredits = useCallback(async () => {
    if (!hasEnoughCredits()) {
      toast.error('You need at least 2 credits to process an audio file');
      return false;
    }
  
    try {
      const axiosInstance = await createAxiosInstance();
      const response = await axiosInstance.post<{ success: boolean }>('/credit/process');
      await fetchCreditStatus(); // Fetch fresh data after processing
      return response.data.success;
    } catch (error) {
      console.error('Failed to process credits:', error);
      toast.error('Failed to process credits');
      return false;
    }
  }, [createAxiosInstance, fetchCreditStatus, hasEnoughCredits]);

  // Refund credits function
  const refundCredits = useCallback(async () => {
    try {
      const axiosInstance = await createAxiosInstance();
      await axiosInstance.post('/credit/refund');
      await fetchCreditStatus(); // Fetch fresh data after refund
      toast.success('Credits refunded successfully');
    } catch (error) {
      console.error('Failed to refund credits:', error);
      toast.error('Failed to refund credits');
    }
  }, [createAxiosInstance, fetchCreditStatus]);

  // Initialize function with debouncing
  const initializeCreditStatus = useCallback(async () => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    await fetchCreditStatus();
  }, [fetchCreditStatus]);

  // Cleanup effect
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);

  // Initial fetch effect
  useEffect(() => {
    initializeCreditStatus();
  }, [initializeCreditStatus]);

  return {
    creditInfo,
    isLoading,
    error,
    fetchCreditStatus,
    processCredits,
    refundCredits,
    hasEnoughCredits,
    initializeCreditStatus
  };
};